import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FilterMenuConfig } from "@/shared/models/filterTypes";
import { useWidgetFilterState } from "@/hooks/useWidgetFilterState";
import { useWidgetHighlightOrder } from "@/hooks/useWidgetHighlightOrder";
import useTabStore from "@/stores/useTabStore";
import {
  effectiveOrder,
  moveKey,
  reconcileOrder,
  type HighlightKey,
} from "@/shared/utils/highlightOrder";
import HighlightPanelToggle from "@/ui-components/FilterPanel/HighlightPanelToggle";
import { SearchFiltersFooterProps } from "@/widget-components/newsquawk-headlines/SearchFiltersFooter";

export interface UseHighlightPanelMenuReturn {
  /** Current highlight selections */
  selections: Record<string, string[]>;
  /** Set highlight selections */
  setSelections: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  /** Whether the panel is open */
  isPanelOpen: boolean;
  /** Toggle panel open/closed */
  togglePanel: () => void;
  /** Open the panel */
  openPanel: () => void;
  /** Close the panel */
  closePanel: () => void;
  /** Filter menu configurations (for highlights) */
  filterMenus: FilterMenuConfig[];
  /** Total count of active selections */
  selectionsCount: number;
  /** Clear all selections */
  clearSelections: () => void;
  /** Footer element with clearSelections injected */
  footerElement: React.ReactElement | null;
  /**
   * The highlighted lists in this widget's order: the first a row matches
   * paints it. See highlightOrder.ts.
   */
  order: HighlightKey[];
  /** Move a highlighted list one place up (-1) or down (+1). */
  moveInOrder: (key: HighlightKey, delta: -1 | 1) => void;
}

const sameOrder = (a: HighlightKey[] | undefined, b: HighlightKey[]) =>
  !!a && a.length === b.length && a.every((key, i) => key === b[i]);

export function useHighlightPanelMenu(
  filterConfig: FilterMenuConfig[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pushAction?: (action: React.ReactElement<any>) => void,
  persistKey?: string,
  footer?: React.ReactElement<SearchFiltersFooterProps>,
  /** Callback when panel opens - use to close other panels */
  onOpen?: () => void,
  /** Close the search panel when this panel opens */
  closeSearch?: () => void,
  /** Register to close this panel when search opens */
  onSearchOpen?: (callback: () => void) => () => void
): UseHighlightPanelMenuReturn {
  const [selections, setRawSelections] = useWidgetFilterState(persistKey, "highlight");
  const [storedOrder, setStoredOrder] = useWidgetHighlightOrder(persistKey);

  /**
   * The selections and order as they are right now, for updates made between
   * renders. A saved widget reads the store, as useWidgetFilterState's own
   * updater does; one outside a tab has only its last render.
   */
  const rendered = useRef({ selections, storedOrder });
  rendered.current = { selections, storedOrder };
  const readCurrent = useCallback(() => {
    if (!persistKey) return rendered.current;
    const state = useTabStore.getState();
    const entry = state.tabs.find((t) => t.id === state.activeTabId)
      ?.widgetFilters?.[persistKey];
    return {
      selections: entry?.highlight ?? {},
      storedOrder: entry?.highlightOrder,
    };
  }, [persistKey]);

  /**
   * Every change of highlights keeps the order in step: what was showing keeps
   * its place, a newly ticked list joins the bottom, an unticked one leaves.
   * Ticks, "Clear active" and the sections that aren't lists all come through
   * here.
   */
  const setSelections = useCallback<
    React.Dispatch<React.SetStateAction<Record<string, string[]>>>
  >(
    (next) => {
      const current = readCurrent();
      const after = typeof next === "function" ? next(current.selections) : next;
      setRawSelections(after);
      const order = reconcileOrder(current.storedOrder, current.selections, after);
      if (!sameOrder(current.storedOrder, order)) setStoredOrder(order);
      if (!persistKey) rendered.current = { selections: after, storedOrder: order };
    },
    [readCurrent, setRawSelections, setStoredOrder, persistKey]
  );

  const order = useMemo(
    () => effectiveOrder(selections, storedOrder),
    [selections, storedOrder]
  );

  const moveInOrder = useCallback(
    (key: HighlightKey, delta: -1 | 1) => {
      const current = readCurrent();
      const from = effectiveOrder(current.selections, current.storedOrder);
      const next = moveKey(from, key, delta);
      if (next !== from) setStoredOrder(next);
    },
    [readCurrent, setStoredOrder]
  );

  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const openPanel = useCallback(() => {
    onOpen?.();
    closeSearch?.();
    setIsPanelOpen(true);
  }, [onOpen, closeSearch]);

  const togglePanel = useCallback(() => {
    if (!isPanelOpen) {
      onOpen?.();
      closeSearch?.();
    }
    setIsPanelOpen((prev) => !prev);
  }, [isPanelOpen, onOpen, closeSearch]);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  // Close this panel when search opens
  useEffect(() => {
    if (!onSearchOpen) return;
    return onSearchOpen(() => setIsPanelOpen(false));
  }, [onSearchOpen]);

  const clearSelections = useCallback(() => {
    setSelections({});
  }, [setSelections]);

  const selectionsCount = useMemo(
    () =>
      Object.entries(selections).reduce(
        (acc, [, items]) => acc + items.length,
        0
      ),
    [selections]
  );

  // Create footer element with clearSelections injected
  const footerElement = useMemo(() => {
    if (!footer) return null;

    return React.cloneElement(footer, {
      clearSelections,
      disableClear: selectionsCount === 0,
    });
  }, [footer, clearSelections, selectionsCount]);

  // Push toggle button to header actions
  useEffect(() => {
    if (!pushAction) return;

    const Toggle = (
      <HighlightPanelToggle
        key="01-highlight-toggle"
        isOpen={isPanelOpen}
        hasSelections={selectionsCount > 0}
        onToggle={togglePanel}
      />
    );

    pushAction(Toggle);
  }, [pushAction, persistKey, isPanelOpen, selectionsCount, togglePanel]);

  return {
    selections,
    setSelections,
    isPanelOpen,
    togglePanel,
    openPanel,
    closePanel,
    filterMenus: filterConfig,
    selectionsCount,
    clearSelections,
    footerElement,
    order,
    moveInOrder,
  };
}
